import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MemberNewsService } from './member-news.service';
import { UIBlockingService } from '../core/ui-blocking.service';
import { ToastNotificationService } from '../core/toast-notification.service';
import { MemberNewsRequest } from './DTOs/member-news-request';
import { FileUploadResult } from './DTOs/file-upload-result';
import { Router } from '@angular/router';
import { CommonLibrary } from '../core/common-library';

@Component({
  selector: 'app-submit-member-news',
    templateUrl: './submit-member-news.component.html',
    providers: [UIBlockingService]
})
export class SubmitMemberNewsComponent {

    constructor(private memberNewsService: MemberNewsService,
        private fb: FormBuilder,
        private notificationService: ToastNotificationService,
        private blockUI: UIBlockingService,
        private router: Router) {
    }
    protected readonly CommonLibrary = CommonLibrary;

    maxMainImageSize = 500000000; // 500 MB
    maxAttachmentSize = 512000000; // 512 MB
    validAttachmentTypes = ["image/jpeg", "image/jpg", "image/gif", "image/png", "application/pdf"];
    invalidDate = false;
    invalidRunDate = false;
    parsedRunDate: Date;
    hasUsedBPArticle = false;
    hasUsedBPArticleDisplay = false;

    articleTitleCtrl = new FormControl("articleTitle", [Validators.required]);
    articleContentCtrl = new FormControl("articleContent", [Validators.required]);
    postingAsCtrl = new FormControl("postingAs", [Validators.required]);
    mainImageCtrl = new FormControl("mainImage", [Validators.required]);
    articleTypeCtrl = new FormControl("articleType", [Validators.required]);
    runDateCtrl = new FormControl("runDate");

    memberNewsForm: FormGroup = new FormGroup({
        articleTitle: this.articleTitleCtrl,
        articleContent: this.articleContentCtrl,
        postingAs: this.postingAsCtrl,
        articleType: this.articleTypeCtrl,
        runDate: this.runDateCtrl,
    });

    mainImageFile: File = null;
    preview = "../../assets/images/member_news_default.png";
    attachments: File[] = [];
    membershipLevel: string = "";

    ngOnInit() {
        this.membershipLevel = localStorage.getItem('membershipLevel');
        this.resetNewsForm();
        this.getBPStatus();
    }

    private resetNewsForm(): void {
        this.memberNewsForm.reset();
        this.memberNewsForm.patchValue({
            articleType: 0,
        });
    }

    checkBPUsed() {
        if (this.hasUsedBPArticle) {
            this.notificationService.warning("You have already used your best practice submission.")
            this.memberNewsForm.get('articleType').setValue(0, { emitEvent: false });
            return;
        }
    }

    
    articleTitle() { return this.memberNewsForm.get('articleTitle').value }
    articleContent() { return this.memberNewsForm.get('articleContent').value }
    postingAs() { return this.memberNewsForm.get('postingAs').value }
    articleType() { return this.memberNewsForm.get('articleType').value }
    runDate() { return this.memberNewsForm.get('runDate').value }

    updateMainImage(target) {
        var files: FileList = target.files;
        var file = files.item(0);

        if (!file.type.includes("image")) {
            this.notificationService.error(file.name + " must be an image file.");
            this.mainImageFile = null;
            this.preview = "../../assets/images/member_news_default.png";
            target.value = null;
            return false;
        }
        if (file.size > this.maxMainImageSize) {
            this.notificationService.error(file.name + " is larger than the maximum file size.");
            this.mainImageFile = null;
            this.preview = "../../assets/images/member_news_default.png";
            target.value = null;
            return false;
        }

        this.mainImageFile = file;


        const reader = new FileReader();
        reader.onload = (e: any) => {
            this.preview = e.target.result;
        }
        reader.readAsDataURL(file);
        return true;
    }

    updateAttachments(target) {
        var files: FileList = target.files;
        for (let i = 0; i < files.length; i++) {
            var file = files.item(i);
            if (!this.validAttachmentTypes.includes(file.type)) {
                this.notificationService.error(file.name + " is not a valid file type.");
                target.value = null;
                continue;
            }
            if (file.size > this.maxAttachmentSize) {
                this.notificationService.error(file.name + " is larger than the maximum file size.");
                target.value = null;
                continue;
            }
            this.attachments.push(file);
        }
    }

    removeAttachment(index: number) {
        if (index >= 0) {
            this.attachments.splice(index, 1);
        }
    }

    submitNews() {
        var error = false;
        this.parsedRunDate = this.parseDate(this.runDate());
        if (this.articleType() == 2) {
            if (this.invalidDate || this.parsedRunDate == null) {
                this.invalidDate = false;
                this.invalidRunDate = true;
                error = true;;
            }
            else {
                this.invalidDate = false;
                this.invalidRunDate = false
            };
        }
        for (var i in this.memberNewsForm.controls) {
            this.memberNewsForm.controls[i].markAsTouched();
        }
        if (!this.memberNewsForm.valid /*|| this.mainImageFile == null*/) {
            this.notificationService.error(`All fields including a main image required for a ${this.articleType() == 1 ? 'news' : 'best practice'} article.`);
            error = true;
        }
        if (error) return;

        this.blockUI.start();

        if (this.mainImageFile != null || this.attachments.length > 0) {
            this.uploadFile()
                .subscribe({
                    next: (data: FileUploadResult) => {
                        this.submitMemberNews(data.mainImageId, data.attachmentIds);
                    },
                    error: (err) => {
                        console.log(err);
                        this.notificationService.error(err.error.message);
                        this.blockUI.stop();
                    }
            });
        }
        else {
            this.submitMemberNews(null, null);
        }
    }

    submitMemberNews(mainImageId: string, attachmentIds: string[]) {
        var request = new MemberNewsRequest(this.articleTitle(), this.articleContent(), this.postingAs(),
            mainImageId, attachmentIds, this.articleType(), this.parsedRunDate);

        this.memberNewsService.submitMemberNews(request)
            .subscribe({
                next: (data) => {
                    console.log('Observable emitted the next value: ' + data);
                },
                error: (err) => {
                    console.log(err);
                    this.notificationService.error(err.error.message);
                    this.blockUI.stop();
                },
                complete: () => {
                    console.log('Observable emitted the complete notification');
                    this.blockUI.stop();
                    this.notificationService.success("News submitted successfully");
                    const url = this.router.url;
                    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                        this.router.navigate([`/${url}`]);
                    })
                }
            });
    }

    uploadFile() {
        const uploadData = new FormData();
        if (this.mainImageFile != null) {
            uploadData.append('mainImage', this.mainImageFile, this.mainImageFile.name);
        }
        for (let i = 0; i < this.attachments.length; i++) {
            let attachment = this.attachments[i];
            uploadData.append("attachment" + i, attachment, attachment.name);
        }
        return this.memberNewsService.uploadNewsFile(uploadData);
    }

    getBPStatus() {
        this.blockUI.start();
        this.memberNewsService.getBPStatus()
            .subscribe(
                {
                    next: (data) => {
                        this.hasUsedBPArticle = data;
                    },
                    error: (err) => {
                        this.notificationService.error(err.message);
                        console.log(err)
                    },
                    complete: () => {
                        this.blockUI.stop();
                    }
                });
    }

    parseDate(dateString: string) {
        if (!dateString) {
            return null;
        }
        const formats = [
            /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
            /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
            /^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY
            /^\d{2}.\d{2}.\d{4}$/, // MM.DD.YYYY
        ];

        let date: Date;

        for (const format of formats) {
            if (format.test(dateString)) {
                if (format === formats[0]) { // YYYY-MM-DD
                    date = new Date(dateString);
                } else if (format === formats[1]) { // MM/DD/YYYY
                    const [month, day, year] = dateString.split('/').map(Number);
                    date = new Date(year, month - 1, day);
                } else if (format === formats[2]) { // MM-DD-YYYY
                    const [month, day, year] = dateString.split('-').map(Number);
                    date = new Date(year, month - 1, day);
                } else if (format === formats[3]) { // MM.DD.YYYY
                    const [month, day, year] = dateString.split('.').map(Number);
                    date = new Date(year, month - 1, day);
                }

                break;
            }
        }
        if (!date || isNaN(date.getTime())) {
            this.invalidDate = true;
        }

        return date;
    }
}
